// src/pages/admin/AdminConfig.jsx

import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Settings, Save, RotateCcw } from 'lucide-react';

const DEFAULT_WEIGHTS = {
  skillWeight: 0.40,
  experienceWeight: 0.25,
  locationWeight: 0.15,
  educationWeight: 0.10,
  circumstanceWeight: 0.10,
};

const WeightSlider = ({ label, value, onChange, description }) => {
  const pct = Math.round(value * 100);
  const color = pct >= 35 ? 'text-tikhet-amber' : pct >= 20 ? 'text-tikhet-sky' : 'text-tikhet-teal';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-600 text-sm text-white">{label}</div>
          <div className="text-xs text-gray-500 mt-0.5">{description}</div>
        </div>
        <span className={`font-mono font-700 text-xl ${color}`}>{pct}%</span>
      </div>
      <div className="relative">
        <input
          type="range"
          min="0"
          max="60"
          step="5"
          value={pct}
          onChange={e => onChange(parseInt(e.target.value) / 100)}
          className="w-full h-2 rounded-full appearance-none cursor-pointer bg-tikhet-muted
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4
                     [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full
                     [&::-webkit-slider-thumb]:bg-tikhet-amber [&::-webkit-slider-thumb]:cursor-pointer"
        />
        <div className="h-2 rounded-full bg-tikhet-amber/60 absolute top-0 left-0 pointer-events-none transition-all"
          style={{ width: `${(pct / 60) * 100}%` }} />
      </div>
    </div>
  );
};

export const AdminConfig = () => {
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getConfig()
      .then(({ data }) => {
        if (data.data) setWeights(data.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalWeight = Math.round(
    (weights.skillWeight + weights.experienceWeight + weights.locationWeight +
     weights.educationWeight + weights.circumstanceWeight) * 100
  );

  const handleSave = async () => {
    if (totalWeight !== 100) {
      toast.error(`Weights must add up to 100%. Currently: ${totalWeight}%`);
      return;
    }
    setSaving(true);
    try {
      await adminAPI.updateConfig(weights);
      toast.success('Matching weights updated successfully');
    } catch { toast.error('Failed to save configuration'); }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setWeights(DEFAULT_WEIGHTS);
    toast.success('Reset to default weights');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size={32} /></div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-2xl">
      <div>
        <h1 className="font-display font-800 text-2xl text-white">Matching Engine Config</h1>
        <p className="text-gray-500 text-sm mt-1">Configure how candidates are scored against job requirements</p>
      </div>

      <div className="card p-6 space-y-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-tikhet-amber/10 flex items-center justify-center">
              <Settings size={18} className="text-tikhet-amber" />
            </div>
            <div>
              <div className="font-display font-600 text-white">Scoring Weights</div>
              <div className="text-xs text-gray-500">Must total 100%</div>
            </div>
          </div>
          <div className={`font-mono font-700 text-lg px-3 py-1 rounded-lg border ${
            totalWeight === 100
              ? 'text-tikhet-teal border-tikhet-teal/30 bg-tikhet-teal/10'
              : 'text-tikhet-rose border-tikhet-rose/30 bg-tikhet-rose/10'
          }`}>
            {totalWeight}%
          </div>
        </div>

        <div className="space-y-6">
          <WeightSlider
            label="Skills Match"
            value={weights.skillWeight}
            onChange={v => setWeights(w => ({ ...w, skillWeight: v }))}
            description="How strongly required skills overlap is weighted"
          />
          <WeightSlider
            label="Experience"
            value={weights.experienceWeight}
            onChange={v => setWeights(w => ({ ...w, experienceWeight: v }))}
            description="Years of experience vs job requirements"
          />
          <WeightSlider
            label="Location"
            value={weights.locationWeight}
            onChange={v => setWeights(w => ({ ...w, locationWeight: v }))}
            description="City match and remote preference compatibility"
          />
          <WeightSlider
            label="Education"
            value={weights.educationWeight}
            onChange={v => setWeights(w => ({ ...w, educationWeight: v }))}
            description="Qualification level match"
          />
          <WeightSlider
            label="Circumstances"
            value={weights.circumstanceWeight}
            onChange={v => setWeights(w => ({ ...w, circumstanceWeight: v }))}
            description="Work rights and availability match"
          />
        </div>

        {/* Visual breakdown */}
        <div className="bg-tikhet-dark rounded-xl p-4 border border-tikhet-border">
          <div className="text-xs font-display font-600 text-gray-400 mb-3 uppercase tracking-wider">Weight Visualisation</div>
          <div className="flex h-6 rounded-lg overflow-hidden gap-0.5">
            {[
              { key: 'skillWeight', label: 'Skills', color: 'bg-tikhet-amber' },
              { key: 'experienceWeight', label: 'Experience', color: 'bg-tikhet-sky' },
              { key: 'locationWeight', label: 'Location', color: 'bg-tikhet-teal' },
              { key: 'educationWeight', label: 'Education', color: 'bg-tikhet-rose' },
              { key: 'circumstanceWeight', label: 'Circumstances', color: 'bg-purple-500' },
            ].map(({ key, color }) => (
              <div key={key}
                className={`${color} transition-all duration-500 rounded-sm`}
                style={{ width: `${weights[key] * 100}%` }}
              />
            ))}
          </div>
          <div className="flex flex-wrap gap-3 mt-2">
            {[
              { key: 'skillWeight', label: 'Skills', color: 'bg-tikhet-amber' },
              { key: 'experienceWeight', label: 'Exp', color: 'bg-tikhet-sky' },
              { key: 'locationWeight', label: 'Location', color: 'bg-tikhet-teal' },
              { key: 'educationWeight', label: 'Education', color: 'bg-tikhet-rose' },
              { key: 'circumstanceWeight', label: 'Circumstances', color: 'bg-purple-500' },
            ].map(({ key, label, color }) => (
              <div key={key} className="flex items-center gap-1.5 text-xs text-gray-500">
                <div className={`w-2 h-2 rounded-full ${color}`} />
                {label} {Math.round(weights[key] * 100)}%
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-3 pt-2 border-t border-tikhet-border">
          <button onClick={handleReset} className="btn-secondary flex items-center gap-2">
            <RotateCcw size={15} /><span>Reset Defaults</span>
          </button>
          <button onClick={handleSave} disabled={saving || totalWeight !== 100}
            className="btn-primary flex-1 flex items-center justify-center gap-2">
            {saving ? <Spinner size={16} /> : <><Save size={15} /><span>Save Configuration</span></>}
          </button>
        </div>
      </div>
    </div>
  );
};
